import { ActionType, ProColumns, ProFormSelect } from '@ant-design/pro-components';
import { DatePicker, Form, Table } from 'antd';
import BoxContainer from 'base/ui/components/BoxContainer';
import CustomButton from 'base/ui/components/Button';
import CustomProTable, { tablePagination } from 'base/ui/components/CustomTable';
import { Filter } from 'base/ui/components/Icons';
import dayjs, { Dayjs } from 'dayjs';
import { cloneDeep } from 'lodash';
import { observer } from 'mobx-react';
import { ExportButton } from 'pages/Components/FunctionButton';
import PageWrapper from 'pages/Components/PageWrapper';
import SaveReportForm from 'pages/Components/reportForm';
import { SearchBox, SearchForm, SearchItem } from 'pages/Components/SearchBox';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HiSearch } from 'react-icons/hi';
import { WasteSummaryReportItem } from 'services/Main/report_services';
import {
  commonMasterDataPresenter,
  commonMasterDataStore,
  commonPresenter,
  commonStore,
  reportPresenter,
  reportStore,
} from 'stores/root_store';
import { DATE_FORMAT_DEFAULT, PAGE_SIZE_DEFAULT, SYSTEM_TITLE } from 'utils/constants';
import { formatDecimalNumber } from 'utils/formatHelper';
import { useResizableColumns } from 'utils/reportHelper';
import { removeEmptyValues } from 'utils/transformHelper';
import { saveSearchParams, getSearchParams } from 'utils/searchParamsHelper';

// Extended type for table display (mapped from WasteSummaryReportItem)
type WasteScrapRow = WasteSummaryReportItem & {
  wasteGroup?: string; // Formatted from wasteGroupingEnvironmentPermitCustomCode | wasteGroupingEnvironmentPermitName
  collectDate?: string; // Mapped from deliveredTime
  quantity?: number; // Mapped from deliveredQuantity
  vendor?: string; // Mapped from vendorName
  status?: string; // Mapped from taskStatusName
};

// Form search payload type
type SearchPayload = {
  wasteGroup?: string | string[];
  wasteGroupingEnvironmentPermitId?: string | string[];
  wasteGroupingContractId?: string | string[];
  wasteProductCode?: string | string[];
  taskStatusCode?: string | string[];
  vendorNumber?: string | string[];
  collectPreset?: string;
  fromDeliveredTime?: string | Dayjs;
  toDeliveredTime?: string | Dayjs;
};

const initialSearch = {
  paging: {
    pageSize: PAGE_SIZE_DEFAULT,
    pageIndex: 1,
    orderBy: undefined,
    orderByDesc: undefined,
  },
  fromDeliveredTime: dayjs().startOf('month').format('YYYY-MM-DD'),
  toDeliveredTime: dayjs().endOf('month').format('YYYY-MM-DD'),
}

// Báo cáo tổng hợp khối lượng chất thải/ phế liệu
const createWasteScrapSummary = () => {
  return observer(() => {
    const { t } = useTranslation();
    document.title = t('report-waste-scrap-summary.title') + ' - ' + SYSTEM_TITLE;

    const [open, setOpen] = useState(false);
    const [searchForm] = Form.useForm<SearchPayload>();
    const [dataPaging, setDataPaging] = useState<any>(initialSearch);

    // disable nhập ngày khi chọn khác "Custom"
    const [disableDatePicker, setDisableDatePicker] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);

    const [dataTable, setDataTable] = useState<WasteScrapRow[]>([])

    const [columns, setColumns] = useState<ProColumns<WasteScrapRow>[]>([
      {
        title: t('report-waste-scrap-summary.columns.stt'),
        dataIndex: 'stt',
        key: 'stt',
        fixed: 'left',
        width: 60,
        align: 'center',
      },
      {
        title: t('report-waste-scrap-summary.columns.waste-group-gpmt'),
        dataIndex: 'wasteGroupingEnvironmentPermitName',
        key: 'wasteGroupingEnvironmentPermitName',
        fixed: 'left',
        width: 250,
      },
      {
        title: t('report-waste-scrap-summary.columns.waste-code'),
        dataIndex: 'wasteProductCode',
        key: 'wasteProductCode',
        fixed: 'left',
        width: 130,
      },
      {
        title: t('report-waste-scrap-summary.columns.waste-name'),
        dataIndex: 'wasteProductName',
        key: 'wasteProductName',
        fixed: 'left',
        width: 200,
      },
      {
        title: t('report-waste-scrap-summary.columns.unit'),
        dataIndex: 'unit',
        key: 'unit',
        width: 100,
        align: 'center',
      },
      {
        title: t('report-waste-scrap-summary.columns.collect-date'),
        dataIndex: 'deliveredTime',
        key: 'deliveredTime',
        width: 140,
        align: 'center',
        render: (_, record) => {
          if (!record.deliveredTime) return '-';
          const date = dayjs(record.deliveredTime);
          return date.isValid() ? date.format('DD/MM/YYYY') : '-';
        },
      },
      {
        title: t('report-waste-scrap-summary.columns.quantity'),
        dataIndex: 'deliveredQuantity',
        key: 'deliveredQuantity',
        width: 170,
        align: 'right',
        render: (value) => formatDecimalNumber(`${value ?? 0}`)
      },
      {
        title: t('report-waste-scrap-summary.columns.vendor'),
        dataIndex: 'vendorName',
        key: 'vendorName',
        width: 300,
        render: (_, record) => record.vendorNumber + ' | ' + (record.vendorName || '-'),
      },
      {
        title: t('report-waste-scrap-summary.columns.status'),
        dataIndex: 'taskStatusName',
        key: 'taskStatusName',
        width: 140,
        align: 'center',
        render: (text, record) => {
          if (!text) return '-';
          const borderColor = record.color ? `${record.color}80` : '#d9d9d9';
          const bgColor = record.color ? `${record.color}1a` : '#fafafa';
          const textColor = record.color || '#000000d9';
          return (
            <div
              className="w-full !text-[10px] !text-center font-medium !py-0.5 border rounded-sm"
              style={{
                borderColor: borderColor,
                color: textColor,
                backgroundColor: bgColor,
              }}
            >
              {text}
            </div>
          );
        },
      },
    ]);

    const tableRef = useRef<ActionType>();
    const constDataName = 'WasteScrapSummary';
    const {
      isResizablePresent,
      setIsResizablePresent,
      resizableColumnSelect,
      columnsState,
      handleColumnsStateChange,
      templateReportModalRef,
      userReports,
      selectedReportId,
      templateReportModalProps,
      handleResize,
    } = useResizableColumns<WasteScrapRow>(columns, setColumns, constDataName);


    // search báo cáo
    const getDataTable = async (dataSearch: any) => {
      await reportPresenter.getWasteSummaryReport(reportStore, dataSearch)
      if (reportStore.wasteSummaryRes?.isSuccess) {
        setDataTable(reportStore.wasteSummaryRes?.data)
        return reportStore.wasteSummaryRes?.data
      }
      return []
    }

    // Load common date preset
    const loadCommonDate = async () => {
      if (!commonStore.listCommonDate.length) {
        await commonPresenter.getListCommonDate(commonStore);
      }
    };

    // When preset changes -> set date range
    const handlePresetChange = async (value?: string) => {
      if (value && value !== 'Custom') {
        await commonPresenter.getDateByCommonDate(commonStore, value);
        setDisableDatePicker(true)
        if (commonStore.commonDate.fromDate) {
          searchForm.setFieldsValue({
            fromDeliveredTime: dayjs(commonStore.commonDate.fromDate, 'YYYY-MM-DD'),
            toDeliveredTime: dayjs(commonStore.commonDate.toDate, 'YYYY-MM-DD'),
          });
        }
      } else if (value === 'Custom') {
        searchForm.setFieldsValue({
          fromDeliveredTime: undefined,
          toDeliveredTime: undefined,
        });
        setDisableDatePicker(false)
      }
    };

    // Initial load: load dropdowns + auto load báo cáo với saved params hoặc preset "ThisMonth"
    useEffect(() => {
      const initializeForm = async () => {
        await loadCommonDate();

        if (!isInitialized) {
          // Kiểm tra xem có saved search params không
          const savedParams = getSearchParams('wasteScrapSummary');

          if (savedParams) {
            // Nếu có saved params, load lại trạng thái search trước đó
            console.log('Loading saved search params:', savedParams);

            let paramsToUse: any = { ...savedParams };

            // Nếu có collectPreset (khác 'Custom'), gọi lại API để lấy ngày hiện tại cho preset đó
            if (savedParams.collectPreset && savedParams.collectPreset !== 'Custom') {
              await commonPresenter.getDateByCommonDate(commonStore, savedParams.collectPreset);
              if (commonStore.commonDate.fromDate) {
                paramsToUse = {
                  ...paramsToUse,
                  fromDeliveredTime: commonStore.commonDate.fromDate,
                  toDeliveredTime: commonStore.commonDate.toDate,
                };
                // Lưu lại params với ngày đã cập nhật
                saveSearchParams('wasteScrapSummary', paramsToUse);
              }
            }

            setDataPaging(paramsToUse);

            // Set form values từ saved params - Convert string dates back to dayjs
            searchForm.setFieldsValue({
              wasteGroup: paramsToUse.wasteGroup,
              wasteGroupingEnvironmentPermitId: paramsToUse.wasteGroupingEnvironmentPermitId,
              wasteGroupingContractId: paramsToUse.wasteGroupingContractId,
              wasteProductCode: paramsToUse.wasteProductCode,
              taskStatusCode: paramsToUse.taskStatusCode,
              vendorNumber: paramsToUse.vendorNumber,
              collectPreset: paramsToUse.collectPreset,
              fromDeliveredTime: paramsToUse.fromDeliveredTime
                ? dayjs(paramsToUse.fromDeliveredTime)
                : undefined,
              toDeliveredTime: paramsToUse.toDeliveredTime
                ? dayjs(paramsToUse.toDeliveredTime)
                : undefined,
            });

            // Set disable state cho date picker
            setDisableDatePicker(paramsToUse.collectPreset && paramsToUse.collectPreset !== 'Custom');

            // Load data với saved params
            tableRef.current?.reload();
            setIsInitialized(true);
          } else {
            // Nếu không có saved params, set preset mặc định "ThisMonth"
            searchForm.setFieldsValue({ collectPreset: 'ThisMonth' });
            await handlePresetChange('ThisMonth');

            const defaultParams = {
              ...initialSearch,
              collectPreset: 'ThisMonth',
              fromDeliveredTime: dayjs().startOf('month').format('YYYY-MM-DD'),
              toDeliveredTime: dayjs().endOf('month').format('YYYY-MM-DD'),
            };

            setDataPaging(defaultParams);
            saveSearchParams('wasteScrapSummary', defaultParams);
            setIsInitialized(true);
          }
        }
      };

      initializeForm();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleExport = async () => {
      // Sử dụng payload từ dataPaging (payload đã được format đúng theo API)
      const exportPayload = { ...dataPaging };

      await reportPresenter.exportWasteSummaryReport(exportPayload);
    };



    const handleResetFilters = async () => {
      searchForm.resetFields();
      initialSearch.paging.pageIndex = 1;
      setDataPaging(initialSearch);
      searchForm.setFieldsValue({
        collectPreset: 'ThisMonth',
        fromDeliveredTime: dayjs().startOf('month'),
        toDeliveredTime: dayjs().endOf('month'),
      });
      setDisableDatePicker(true);
      searchForm.submit();
    };



    return (
      <PageWrapper
        breadcrumb={[{
          text: t('report-waste-scrap-summary.title'),
        }]}
        extras={
          <>
            <ExportButton loading={false} onClick={handleExport} />
            <CustomButton onClick={() => setOpen(true)} type="primary" color="#EFF4F9" className="!px-0 w-8">
              <Filter className="!fill-primary" />
            </CustomButton>
          </>
        }
      >
        <BoxContainer>
          <style>{`
                        .report-table .ant-table-tbody > tr:hover > td {
                            background: #fafafa;
                        }
                        .report-table .ant-table-summary > tr > td {
                            background: #E5E5E6 !important;
                        }
                    `}</style>
          <CustomProTable<WasteScrapRow>
            className="report-table"
            bordered
            columns={columns}
            dataSource={dataTable}
            actionRef={tableRef}
            tableAlertRender={false}
            handleResize={handleResize}
            isResizablePresent={isResizablePresent}
            resizableColumnSelect={resizableColumnSelect}
            columnsState={{
              value: Object.keys(columnsState).length > 0 ? columnsState : undefined,
              onChange: (newColumnsState) => {
                handleColumnsStateChange(newColumnsState);
              },
            }}

            request={async (params = {}, sort, filter) => {
              const data = await getDataTable({
                ...dataPaging,
                paging: {
                  ...dataPaging.paging,
                  orderBy:
                    Object.values(sort)[0] === "ascend"
                      ? `${Object.keys(sort)[0].split(",").pop()}`
                      : undefined,
                  orderByDesc:
                    Object.values(sort)[0] === "descend"
                      ? `${Object.keys(sort)[0].split(",").pop()}`
                      : undefined,
                },
              });
              if (Object.values(sort).length) {

                setDataPaging({
                  ...dataPaging,
                  paging: {
                    ...dataPaging.paging,
                    orderBy:
                      Object.values(sort)[0] === "ascend"
                        ? `${Object.keys(sort)[0].split(",").pop()}`
                        : undefined,
                    orderByDesc:
                      Object.values(sort)[0] === "descend"
                        ? `${Object.keys(sort)[0].split(",").pop()}`
                        : undefined,
                  },
                });
              }
              return {
                data: data,
                success: reportStore.wasteHandoverRegistrationRes?.isSuccess
              };

            }}
            toolBarRender={() => [
              <SaveReportForm
                key="save-report"
                setIsResizablePresent={setIsResizablePresent}
                templateReportModalRef={templateReportModalRef}
                userReports={userReports}
                selectedReportId={selectedReportId}
                templateReportModalProps={templateReportModalProps}
              />,
            ]}
            rowKey={(record) => `row-${record.stt}`}
            pagination={{
              ...tablePagination(dataPaging, setDataPaging),
              total: reportStore.wasteSummaryRes?.paging?.recordsTotal,
            }}
            summary={(pageData) => {
              return (
                <Table.Summary fixed>
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0} colSpan={4}>
                      <span className="font-semibold text-right block">
                        {t('report-waste-scrap-summary.total-label')}
                      </span>
                    </Table.Summary.Cell>

                    <Table.Summary.Cell index={4} />
                    <Table.Summary.Cell index={5} />

                    <Table.Summary.Cell index={6}>
                      <span className="font-semibold text-right block">
                        {reportStore.wasteSummaryTotalQuantity !== undefined && reportStore.wasteSummaryTotalQuantity !== null
                          ?
                          formatDecimalNumber(`${reportStore.wasteSummaryTotalQuantity ?? 0}`)
                          : '-'}
                      </span>
                    </Table.Summary.Cell>

                    <Table.Summary.Cell index={7} />
                    <Table.Summary.Cell index={8} />
                  </Table.Summary.Row>
                </Table.Summary>
              );
            }}
          />
        </BoxContainer>

        <SearchBox
          onClose={() => setOpen(false)}
          className="lg:!w-[50%]"
          open={open}
          footer={
            <div className='flex gap-2 justify-end'>
              <CustomButton
                onClick={() => {
                  handleResetFilters();
                  setOpen(false);
                }}
              >
                {'Reset tìm kiếm'}
              </CustomButton>
              <CustomButton
                htmlType="submit"
                // className="my-auto ml-auto"
                type="primary"
                icon={<HiSearch />}
                onClick={() => {
                  searchForm.submit();
                }}
              >
                Tìm kiếm
              </CustomButton>
            </div>
          }
        >
          <SearchForm
            form={searchForm}
            initialValues={{
              collectPreset: 'ThisMonth',
            }}
            className=" !gap-y-4 !pb-24"
            onFinish={(values) => {
              const valueClone = cloneDeep(values)
              // convert lại from date to date
              const fromDeliveredTime = valueClone?.fromDeliveredTime ? valueClone.fromDeliveredTime?.format("YYYY-MM-DD") : undefined
              const toDeliveredTime = valueClone?.toDeliveredTime ? valueClone.toDeliveredTime?.format("YYYY-MM-DD") : undefined
              Object.assign(valueClone, { fromDeliveredTime, toDeliveredTime })
              console.log('valueCloneeeeeeeeeeeeeeeeeeeee🥽🥽🥽🥽🥽', valueClone);

              // bỏ các giá trị rỗng null và trim giá trị
              removeEmptyValues(valueClone)
              const dataSearch: any = { paging: { ...dataPaging.paging, pageIndex: 1 }, ...valueClone }
              setDataPaging(dataSearch)

              // Lưu search params vào localStorage
              saveSearchParams('wasteScrapSummary', dataSearch)

              setOpen(false)
              tableRef.current?.reload()
            }}
          >
            {/* Nhóm chất thải */}
            <SearchItem name="wasteGroup" label={t('report-waste-scrap-summary.search.waste-group')}>
              <ProFormSelect
                allowClear
                mode="multiple"
                request={async () => {
                  await commonMasterDataPresenter.getCommonDataByKey(commonMasterDataStore, 'WasteType');
                  return (commonMasterDataStore.commonRespone?.data || []).map((item: any) => ({
                    label: item.value || '',
                    value: item.key || '',
                  }));
                }}
              />
            </SearchItem>
            {/* Phân nhóm chất thải (GPMT) */}
            <SearchItem name="wasteGroupingEnvironmentPermitId" label={t('report-waste-scrap-summary.search.waste-subgroup-gpmt')}>
              <ProFormSelect
                allowClear
                mode="multiple"
                showSearch
                debounceTime={300}
                request={async ({ keyWords: keyword }) => {
                  await commonMasterDataPresenter.getDropdownWasteGroupingEnvironmentPermit(commonMasterDataStore, false);
                  return (commonMasterDataStore.wasteGroupingEnvironmentPermitOptions || [])
                    .filter((item: any) => {
                      if (!keyword) return true;
                      const searchText = keyword.toLowerCase();
                      return (item.value || '').toLowerCase().includes(searchText);
                    })
                    .map((item: any) => ({
                      label: item.value || '',
                      value: item.key || '',
                    }));
                }}
              />
            </SearchItem>
            {/* Hợp đồng chất thải/ phế liệu */}
            <SearchItem name="wasteGroupingContractId" label={t('report-waste-scrap-summary.search.waste-subgroup-hd')}>
              <ProFormSelect
                allowClear
                mode="multiple"
                showSearch
                debounceTime={300}
                request={async ({ keyWords: keyword }) => {
                  await commonMasterDataPresenter.getDropdownWasteGroupingContract(commonMasterDataStore, false);
                  return (commonMasterDataStore.wasteGroupingContractOptions || [])
                    .filter((item: any) => {
                      if (!keyword) return true;
                      const searchText = keyword.toLowerCase();
                      return (item.value || '').toLowerCase().includes(searchText);
                    })
                    .map((item: any) => ({
                      label: item.value || '',
                      value: item.key || '',
                    }));
                }}
              />
            </SearchItem>
            {/* Chất thải/ phế liệu */}
            <SearchItem name="wasteProductCode" label={t('report-waste-scrap-summary.search.waste-code')}>
              <ProFormSelect
                allowClear
                mode="multiple"
                showSearch
                debounceTime={300}
                request={async ({ keyWords: keyword }) => {
                  const result = await commonPresenter.getDropdownProductWaste(keyword);
                  return (result || []).map((item: any) => ({
                    label: item.value || '',
                    value: item.key || '',
                  }));
                }}
              />
            </SearchItem>
            {/* Tình trạng phiếu */}
            <SearchItem name="taskStatusCode" label={'Tình trạng phiếu'}>
              <ProFormSelect
                allowClear
                mode='multiple'
                request={async ({ keyWords: keyword }) => {
                  await commonPresenter.getDropdownWasteMaterialTaskStatus(commonStore);
                  return commonStore.dropdownWasteMaterialTaskStatus.map((item: any) => ({
                    label: item.value || '',
                    value: item.key || '',
                  }));
                }}
              />
            </SearchItem>
            {/* Nhà cung cấp */}
            <SearchItem name="vendorNumber" label={t('report-waste-scrap-summary.search.vendor')}>
              <ProFormSelect
                allowClear
                mode='multiple'
                showSearch
                debounceTime={300}
                request={async ({ keyWords: keyword }) => {
                  console.log({ keyword });
                  await commonPresenter.getDropdownVendor(commonStore, { keyword, takeNumber: 20, type: 'NCC_CT' });
                  return commonStore.commonRespone.data.map((item: any) => ({
                    label: item.value || '',
                    value: item.key || '',
                  }));
                }}
              />
            </SearchItem>
            {/* Ngày thu gom preset */}
            <SearchItem name="collectPreset" label={t('report-waste-scrap-summary.search.collect-preset')}>
              <ProFormSelect
                allowClear
                request={async () => {
                  if (!commonStore.listCommonDate.length) {
                    await commonPresenter.getListCommonDate(commonStore);
                  }
                  return commonStore.listCommonDate.map((item: any) => ({
                    label: item.value || '',
                    value: item.key || '',
                  }));
                }}
                onChange={(v) => handlePresetChange(v as string)}
              />
            </SearchItem>
            <SearchItem></SearchItem>
            {/* Từ ngày */}
            <SearchItem name="fromDeliveredTime" label={t('report-waste-scrap-summary.search.from-date')}>
              <DatePicker changeOnBlur disabled={disableDatePicker} format={DATE_FORMAT_DEFAULT} className="w-full" />
            </SearchItem>
            {/* Đến ngày */}
            <SearchItem name="toDeliveredTime" label={t('report-waste-scrap-summary.search.to-date')}>
              <DatePicker changeOnBlur disabled={disableDatePicker} format={DATE_FORMAT_DEFAULT} className="w-full" />
            </SearchItem>
          </SearchForm>
        </SearchBox>
      </PageWrapper>
    );
  });
};

export default createWasteScrapSummary;


